import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Tooltip,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import type { ThemeMode } from '../../App';
import { BUILD_DATE } from '../../version';

interface AppBarProps {
  onMenuClick: () => void;
  themeMode: ThemeMode;
  onThemeModeChange: (mode: ThemeMode) => void;
  onDebugToggle: () => void;
}

export function AppBar({
  onMenuClick,
  themeMode,
  onThemeModeChange,
  onDebugToggle,
}: AppBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Cycle through theme modes: light -> auto -> dark -> light
  const cycleTheme = () => {
    const modes: ThemeMode[] = ['light', 'auto', 'dark'];
    const currentIndex = modes.indexOf(themeMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    onThemeModeChange(modes[nextIndex]);
  };

  // Get the icon and tooltip for current theme mode
  const getThemeIcon = () => {
    switch (themeMode) {
      case 'light':
        return <Brightness7Icon sx={{ fontSize: 20 }} />;
      case 'dark':
        return <Brightness4Icon sx={{ fontSize: 20 }} />;
      case 'auto':
      default:
        return <BrightnessAutoIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getThemeTooltip = () => {
    switch (themeMode) {
      case 'light':
        return 'Light mode (click for auto)';
      case 'dark':
        return 'Dark mode (click for light)';
      case 'auto':
      default:
        return 'Auto mode (click for dark)';
    }
  };

  return (
      <MuiAppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {/* Hamburger menu - only on mobile */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon sx={{ color: 'text.primary' }} />
          </IconButton>

          <RocketLaunchIcon sx={{ color: 'primary.main' }} />

          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              color: 'text.primary',
              fontWeight: 600,
            }}
          >
            Space Antics
          </Typography>

          {/* Build date - desktop only */}
          {!isMobile && (
            <Tooltip title={`Built: ${new Date(BUILD_DATE === 'development' ? Date.now() : BUILD_DATE).toLocaleString()} (double-click to toggle debug panels)`}>
              <Chip
                label={new Date(BUILD_DATE === 'development' ? Date.now() : BUILD_DATE).toLocaleString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                size="small"
                variant="outlined"
                onDoubleClick={onDebugToggle}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  '& .MuiChip-label': { px: 1 },
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              />
            </Tooltip>
          )}

          {/* Theme toggle - hidden on mobile (in drawer instead) */}
          {!isMobile && (
            <Tooltip title={getThemeTooltip()}>
              <IconButton
                color="inherit"
                aria-label="toggle theme"
                onClick={cycleTheme}
                sx={{ color: 'text.secondary' }}
              >
                {getThemeIcon()}
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </MuiAppBar>
  );
}
